import React from 'react';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { DocumentPlusGlyph } from './assets/document-plus-glyph';
import { GenericPlusGlyph } from './assets/generic-plus-glyph';
import { PresentationPlusGlyph } from './assets/presentation-plus-glyph';
import messages from './messages';

export type DefaultCategoryInfo = {
	i18nMessage: MessageDescriptorV2;
	placeholderIcon: JSX.Element;
	analyticsAttributeName: string;
};

export const CommunicationCategory: DefaultCategoryInfo = {
	i18nMessage: messages.communicationCategory,
	placeholderIcon: <DocumentPlusGlyph />,
	analyticsAttributeName: 'numLinksInCommunicationCategory',
};

export const RolloutCategory: DefaultCategoryInfo = {
	i18nMessage: messages.rolloutCategory,
	placeholderIcon: <GenericPlusGlyph />,
	analyticsAttributeName: 'numLinksInRolloutCategory',
};

export const TestingCategory: DefaultCategoryInfo = {
	i18nMessage: messages.testingCategory,
	placeholderIcon: <DocumentPlusGlyph />,
	analyticsAttributeName: 'numLinksInTestingCategory',
};

export const AnalyticsCategory: DefaultCategoryInfo = {
	i18nMessage: messages.analyticsCategory,
	placeholderIcon: <PresentationPlusGlyph />,
	analyticsAttributeName: 'numLinksInAnalyticsCategory',
};

export const DevelopmentCategory: DefaultCategoryInfo = {
	i18nMessage: messages.developmentCategory,
	placeholderIcon: <GenericPlusGlyph />,
	analyticsAttributeName: 'numLinksInDevelopmentCategory',
};

export const SupportCategory: DefaultCategoryInfo = {
	i18nMessage: messages.supportCategory,
	placeholderIcon: <GenericPlusGlyph />,
	analyticsAttributeName: 'numLinksInSupportCategory',
};

export const DEFAULT_CATEGORIES = [
	CommunicationCategory,
	RolloutCategory,
	TestingCategory,
	AnalyticsCategory,
	DevelopmentCategory,
	SupportCategory,
];

export const useFormatCategoryName = () => {
	const { formatMessage } = useIntl();
	return (defaultCategoryInfo: DefaultCategoryInfo): string =>
		formatMessage(defaultCategoryInfo.i18nMessage);
};

export const usePlaceholderIcon = (categoryName: string): JSX.Element => {
	const formatCategoryName = useFormatCategoryName();
	// This is a temporary approach that will fall back to a generic icon if the placeholder was created in a different
	// locale from the one it's being viewed in. A more robust enum-based approach should be used once related work items
	// are migrated out of Data Depot and into ERS.
	const defaultCategory = DEFAULT_CATEGORIES.find(
		(category) => formatCategoryName(category) === categoryName,
	);
	return defaultCategory?.placeholderIcon ? defaultCategory.placeholderIcon : <GenericPlusGlyph />;
};
